import React, { useState, useRef, useEffect } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

export interface SelectOption {
    value: string;
    label: string | ReactNode;
    icon?: ReactNode;
}

interface SelectProps {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    className?: string;
    dropdownClassName?: string;
    disabled?: boolean;
}

export function Select({
    value,
    onChange,
    options,
    placeholder = 'Selecione...',
    className,
    dropdownClassName,
    disabled = false
}: SelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(opt => opt.value === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false);
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    const handleToggle = () => {
        if (!isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownHeight = Math.min(options.length * 36 + 8, 248); // max-h-60 = 240px + padding
            setOpenUpward(spaceBelow < dropdownHeight + 16);
        }
        setIsOpen(!isOpen);
    };

    return (
        <div ref={containerRef} className="relative inline-block text-left">
            <button
                type="button"
                disabled={disabled}
                onClick={handleToggle}
                className={twMerge(
                    "inline-flex items-center justify-between gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-indigo-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                    value ? "border-indigo-300 bg-indigo-50/50" : "",
                    className
                )}
            >
                <span className="flex items-center gap-2 truncate">
                    {selectedOption?.icon}
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown 
                    size={16} 
                    className={clsx(
                        "text-slate-400 transition-transform duration-200", 
                        isOpen ? "rotate-180 text-indigo-500" : ""
                    )} 
                />
            </button>

            {/* Dropdown Menu */}
            <div
                className={clsx(
                    "absolute z-50 min-w-full w-max max-w-xs rounded-xl bg-white shadow-lg border border-slate-100 py-1 transition-all",
                    openUpward
                        ? "bottom-full mb-2 origin-bottom-left"
                        : "top-full mt-2 origin-top-left",
                    isOpen
                        ? "opacity-100 scale-100 translate-y-0 visible"
                        : clsx("opacity-0 scale-95 invisible", openUpward ? "translate-y-2" : "-translate-y-2"),
                    dropdownClassName
                )}
            >
                <div className="max-h-60 overflow-y-auto [scrollbar-width:thin]">
                    {options.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-slate-500 text-center">Nenhuma opção</div>
                    ) : (
                        options.map((option) => {
                            const isSelected = option.value === value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => {
                                        onChange(option.value);
                                        setIsOpen(false);
                                    }}
                                    className={clsx(
                                        "w-full text-left px-4 py-2 text-sm flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors",
                                        isSelected ? "text-indigo-600 bg-indigo-50/30 font-medium" : "text-slate-700"
                                    )}
                                >
                                    <span className="flex items-center gap-2 truncate">
                                        {option.icon}
                                        {option.label}
                                    </span>
                                    {isSelected && <Check size={16} className="text-indigo-600 shrink-0" />}
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
